/**
 * ReviewNoteComposer — the note both review transitions require (BON-381).
 *
 * One composer for resolve AND reopen: the note becomes the record of the
 * remedy, stamped with the operator and the moment, so neither transition may
 * be taken without one. That requirement is precisely why resolve is not a
 * queue-row button — a row has nowhere to put this.
 */
import { Check, RotateCcw } from 'lucide-react';
import { useEffect, useRef } from 'react';

import { NOTE_MAX_LENGTH } from './reviewQueueModel';
import type { ComposerMode } from './useReviewDetailState';

export interface ReviewNoteComposerProps {
  mode: ComposerMode;
  note: string;
  onNoteChange: (next: string) => void;
  /** Validation message, shown after a refused submission. */
  noteMessage: string | null;
  /** A failed write (e.g. the 409 two operators raced into). */
  actionError: string | null;
  submitting: boolean;
  onSubmit: () => void;
  onCancel: () => void;
}

const COPY = {
  resolve: {
    heading: 'Mark resolved',
    hint: 'Stamped with your name · removes it from the queue',
    confirm: 'Confirm resolved',
  },
  reopen: {
    heading: 'Reopen this item',
    hint: 'Stamped with your name · returns it to the queue',
    confirm: 'Confirm reopen',
  },
} as const;

const HEADING_STYLE = {
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: '.05em',
  textTransform: 'uppercase',
  color: 'var(--nb-subtle)',
} as const;

const TEXTAREA_STYLE = {
  width: '100%',
  boxSizing: 'border-box',
  resize: 'vertical',
  fontFamily: 'inherit',
  fontSize: 12.5,
  lineHeight: 1.5,
  color: 'var(--nb-ink)',
  background: 'var(--nb-bg)',
  borderRadius: 8,
  padding: '9px 11px',
} as const;

const PLACEHOLDER =
  'What did you do to fix it? This is stamped with your name and stays on the record.';

function Problem({ message, id }: { message: string; id?: string }) {
  return (
    <div id={id} role="alert" style={{ fontSize: 11.5, color: 'var(--nb-warn)', fontWeight: 600 }}>
      {message}
    </div>
  );
}

/**
 * The composer's refusal, shown when there is no composer.
 *
 * A conflict CLOSES the composer — the action it was composing no longer applies
 * — and refetches. Rendering the message inside the composer therefore lost it
 * exactly when it mattered: the page silently changed under the operator with no
 * word about why. This takes the composer's own slot, so the explanation appears
 * where they were already looking.
 */
export function ReviewActionNotice({ message }: { message: string | null }) {
  return message ? <Problem message={message} /> : null;
}

export function ReviewNoteComposer(props: ReviewNoteComposerProps) {
  const { mode, note, onNoteChange, noteMessage, actionError, submitting } = props;
  const copy = COPY[mode];
  const Icon = mode === 'resolve' ? Check : RotateCcw;
  const box = useRef<HTMLTextAreaElement>(null);
  const card = useRef<HTMLElement>(null);

  // U1 + U2: focus the note the moment the composer appears. Pressing "Mark
  // resolved" used to leave focus on a button that had just been removed, which
  // drops it to <body> — a keyboard user then had to tab from the top of the
  // document to reach the box the click was asking them to fill in.
  //
  // And on a phone the action bar is FIXED at the bottom while this card renders
  // near the top, so tapping resolve from the foot of a long page changed
  // something entirely off screen and read as a no-op. Scrolling to it is what
  // makes the two ends of the page agree.
  useEffect(() => {
    box.current?.focus();
    // Optional-called: happy-dom does not implement scrollIntoView, and a missing
    // scroll must never break the composer.
    card.current?.scrollIntoView?.({ block: 'nearest' });
  }, []);

  const errorId = noteMessage ? `${mode}-note-error` : undefined;

  return (
    <section
      ref={card}
      className="nb-card"
      style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
    >
      <div style={HEADING_STYLE}>{copy.heading}</div>
      <textarea
        ref={box}
        aria-label="Review note"
        // The refusal has to be REACHABLE from the field, not merely near it:
        // `aria-describedby` is what makes a screen reader read the reason after
        // the label, and `aria-invalid` is what marks the field as the problem.
        aria-invalid={noteMessage ? true : undefined}
        aria-describedby={errorId}
        value={note}
        rows={3}
        maxLength={NOTE_MAX_LENGTH}
        onChange={(e) => onNoteChange(e.target.value)}
        placeholder={PLACEHOLDER}
        style={{
          ...TEXTAREA_STYLE,
          border: `1px solid ${noteMessage ? 'var(--nb-warn)' : 'var(--nb-border)'}`,
        }}
      />
      {noteMessage && <Problem message={noteMessage} id={errorId} />}
      {actionError && <Problem message={actionError} />}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ flex: 1, fontSize: 11, color: 'var(--nb-subtle)', minWidth: 160 }}>
          {copy.hint}
        </span>
        <button
          type="button"
          className="nb-btn nb-btn-ghost"
          style={{ height: 30, fontSize: 12 }}
          onClick={props.onCancel}
        >
          Cancel
        </button>
        <button
          type="button"
          className="nb-btn nb-btn-primary"
          style={{ height: 30, fontSize: 12 }}
          disabled={submitting}
          onClick={props.onSubmit}
        >
          <Icon size={13} />
          {copy.confirm}
        </button>
      </div>
    </section>
  );
}
